'use client'

import { useId, useMemo } from 'react'
import { useAudioWaveform } from '@/lib/hooks/use-audio-waveform'
import type { AssetSummary } from '@/lib/assets/types'
import styles from './preview-player-bar.module.scss'

export function PreviewPlayerWaveform({ asset, progress }: { asset?: AssetSummary | null; progress: number }) {
  const clipId = useId()
  const { data } = useAudioWaveform(asset)
  const path = useMemo(() => {
    if (!data?.length) return 'M0 12H360'
    const maximum = data.reduce((peak, value) => Math.max(peak, Math.abs(value)), 0) || 1
    return Array.from({ length: 72 }, (_, index) => {
      const start = Math.floor((index * data.length) / 72)
      const end = Math.max(start + 1, Math.floor(((index + 1) * data.length) / 72))
      let peak = 0
      for (let sample = start; sample < end; sample++) peak = Math.max(peak, Math.abs(data[sample] ?? 0))
      const height = Math.max(1, (peak / maximum) * 10)
      return `M${index * 5 + 2} ${12 - height}v${height * 2}`
    }).join(' ')
  }, [data])

  return (
    <svg
      className={styles.waveform}
      viewBox="0 0 360 24"
      preserveAspectRatio="none"
      aria-hidden="true"
      data-waveform={!!data?.length}
    >
      <defs>
        <clipPath id={clipId}>
          <rect width={`${Math.min(100, Math.max(0, progress))}%`} height="24" />
        </clipPath>
      </defs>
      <path d={path} fill="none" stroke="#454954" strokeWidth="2.5" strokeLinecap="round" />
      <g clipPath={`url(#${clipId})`}>
        <path d={path} fill="none" stroke="#ff842b" strokeWidth="2.5" strokeLinecap="round" />
      </g>
    </svg>
  )
}
